'use client';

/**
 * HomePipelineCard — RFQ pipeline funnel + nudges.
 *
 * Reads `pipeline_summary` (Draft / Sent / Purchase / Done counts +
 * action nudges). Tile click currently uses the Zustand
 * `pipelineStageFocus` mechanic to deep-link into obeya/kanban —
 * preserved during mode1 ⇄ obeya cohabitation. Will replace with
 * URL-driven deep-links in the next arc; the click handler is the
 * one place that needs to change when that lands.
 *
 * @see klyde_edge/composition/cards/home.py — content_key="home_pipeline"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Home
 * @layer: presentation
 */

import { ChevronRight, Send, Zap, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useNavItems, useAffordance } from '@/lib/stores/total-matrix-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { CardShell } from '../card-shell';

interface PipelineSummaryResponse {
  stages: { state: string; count: number; with_responses: number; label: string }[];
  total: number;
  nudges: string[];
  labels: Record<string, string>;
}

const STAGE_STYLE: { state: string; color: string; bgColor: string }[] = [
  { state: 'draft',    color: 'text-muted-foreground', bgColor: 'bg-muted' },
  { state: 'sent',     color: 'text-info',             bgColor: 'bg-info/10' },
  { state: 'purchase', color: 'text-warning',          bgColor: 'bg-warning/10' },
  { state: 'done',     color: 'text-success',          bgColor: 'bg-success/10' },
];

export function HomePipelineCard() {
  const setPipelineStageFocus = useLayoutStore((s) => s.setPipelineStageFocus);
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  const allNavItems = useNavItems();
  const lang = useLanguageStore((s) => s.lang) ?? undefined;
  const params = lang ? { lang } : {};

  // Pipeline tile target — derived from the active mode's
  // `redirects_pipeline_to` field (set on the `@mode` decoration in
  // the composition registry). Replaces the previous React-local
  // `pipelineTargetMode = 'mode1'` constant.
  // Today mode2 declares `redirects_pipeline_to='mode1'`; legacy
  // `home` mode has no redirect declared so the fallback is `obeya`
  // (its existing behaviour). Will become URL deep-links in the next
  // arc; the redirect-target field stays as a routing hint.
  const activeNav = allNavItems.find((n) => n.mode === activeMode);
  const pipelineTargetMode: string = activeNav?.redirects_pipeline_to ?? 'obeya';

  const launchProjectsWithStageFocus = (stageState: string | null) => {
    setPipelineStageFocus(stageState);
    switchExplorerMode(pipelineTargetMode);
  };

  const { data: resp, isLoading } = useStrategyData<PipelineSummaryResponse>('pipeline_summary', params);
  const loadingTestId = useAffordance('home.pipeline.loading');
  const data = resp?.success ? resp.data : undefined;
  const stageMap = new Map((data?.stages ?? []).map((s) => [s.state, s]));
  const stages = STAGE_STYLE.map((style) => {
    const s = stageMap.get(style.state);
    return {
      id: style.state,
      label: s?.label ?? '',
      count: s?.count ?? 0,
      color: style.color,
      bgColor: style.bgColor,
    };
  });
  const nudges = data?.nudges ?? [];
  const L = data?.labels ?? {};
  // Lead with the stage carrying the most volume — the number that matters.
  const lead = stages.reduce((a, b) => (b.count > a.count ? b : a), stages[0]);

  return (
    <CardShell style={{ fontSize: surfaceBase.spacious }} className="overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" />
          <h3 className={text.heading}>{L.pipeline_title ?? 'My Pipeline'}</h3>
        </div>
        <button
          onClick={() => switchExplorerMode(pipelineTargetMode)}
          className={cn(text.muted, 'hover:text-foreground transition-colors cursor-pointer')}
        >
          {L.view_all ?? 'View all'}
        </button>
      </div>
      <div className="flex-1 p-4 space-y-4">
        {isLoading ? (
          <div data-testid={loadingTestId} className="space-y-4">
            <div className="flex items-center gap-1">
              {STAGE_STYLE.map((style, i) => (
                <div key={style.state} className="flex items-center gap-1 flex-1">
                  <div className="flex-1 rounded-lg p-2.5 text-center bg-muted animate-pulse">
                    <div className="h-6 w-6 mx-auto rounded bg-muted-foreground/10 mb-1" />
                    <div className="h-2 w-10 mx-auto rounded bg-muted-foreground/10" />
                  </div>
                  {i < STAGE_STYLE.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-muted-foreground/20 shrink-0" />
                  )}
                </div>
              ))}
            </div>
            <div className="h-1.5 rounded-full bg-muted animate-pulse" />
          </div>
        ) : (
          <>
            {/* Lead with the number that matters — the largest thing in the
               card, in primary. The old four equal tiles hid the funnel shape;
               this makes the volume the message. */}
            <div className="flex items-baseline gap-2">
              <span className={cn(text.display, 'tabular-nums leading-none text-primary')}>{lead.count}</span>
              <span className={text.muted}>{lead.label}</span>
            </div>
            {/* ONE proportional bar (flex:count) — the biggest stage dwarfs the
               rest, so the funnel reads at a glance. Empty stages are neutral
               stubs, NOT tinted (a 0 has no state to colour). Non-zero stages
               read as one primary family with the lead solid, the rest lighter. */}
            <div className="flex items-stretch h-8 rounded-md overflow-hidden border border-border gap-px">
              {stages.map((stage) =>
                stage.count > 0 ? (
                  <div
                    key={stage.id}
                    onClick={() => launchProjectsWithStageFocus(stage.id)}
                    title={`${stage.count} ${stage.label}`}
                    style={{ flex: stage.count }}
                    className={cn(
                      'flex items-center justify-center min-w-0 cursor-pointer text-primary-foreground tabular-nums font-semibold text-xs hover:opacity-90 transition-opacity',
                      stage.id === lead.id ? 'bg-primary' : 'bg-primary/60',
                    )}
                  >
                    {stage.count}
                  </div>
                ) : (
                  <div
                    key={stage.id}
                    title={`0 ${stage.label}`}
                    className={cn(text.muted, 'flex items-center justify-center shrink-0 w-9 bg-muted tabular-nums')}
                  >
                    0
                  </div>
                ),
              )}
            </div>
            {/* Legend names each band + carries its count. Labels are
               server-driven (stage.label); counts are data. */}
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {stages.map((stage) => (
                <span key={stage.id} className={cn(text.muted, 'flex items-center gap-1.5')}>
                  <span
                    className={cn(
                      'w-2 h-2 rounded-sm shrink-0',
                      stage.count > 0
                        ? stage.id === lead.id ? 'bg-primary' : 'bg-primary/60'
                        : 'bg-muted border border-border',
                    )}
                  />
                  {stage.label}
                  <span className="tabular-nums font-medium text-foreground">{stage.count}</span>
                </span>
              ))}
            </div>
            {nudges.length > 0 && (
              <div className="space-y-1.5">
                {nudges.map((nudge, i) => (
                  <button
                    key={i}
                    onClick={() => launchProjectsWithStageFocus(null)}
                    className="w-full flex items-center gap-2 text-left group cursor-pointer"
                  >
                    <Zap className="w-3 h-3 text-warning" />
                    <span className={cn(text.muted, 'group-hover:text-foreground transition-colors')}>
                      {nudge}
                    </span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground/40 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </CardShell>
  );
}
